import React, {
  CSSProperties,
  FunctionComponent,
  useMemo,
  useState,
} from 'react'
import { iconCategories } from 'Shared/components/PuxIcon/iconCategories'
import PuxIcon, {
  IconColor,
  IconName,
  IconSize,
} from 'Shared/components/PuxIcon/PuxIcon'

const ICON_SIZES: IconSize[] = [12, 16, 24, 32, 48, 64]
const ICON_COLORS: IconColor[] = [
  'default',
  'muted',
  'accent',
  'inverse',
  'success',
  'error',
  'current',
]

const pageStyle: CSSProperties = {
  minHeight: '100vh',
  padding: '4rem 2rem 8rem',
  backgroundColor: 'var(--color-surface-subtle)',
  color: 'var(--color-text-primary)',
}

const shellStyle: CSSProperties = {
  display: 'grid',
  gap: '3.2rem',
  maxWidth: '128rem',
  margin: '0 auto',
}

const headerStyle: CSSProperties = {
  display: 'grid',
  gap: '1.2rem',
}

const controlsStyle: CSSProperties = {
  display: 'grid',
  gap: '1.2rem',
  gridTemplateColumns: 'repeat(auto-fit, minmax(18rem, 1fr))',
  alignItems: 'end',
  padding: '1.6rem',
  border: '1px solid var(--color-border-subtle)',
  borderRadius: 'var(--Radius-Component-Card)',
  backgroundColor: 'var(--color-surface-default1)',
}

const fieldStyle: CSSProperties = {
  display: 'grid',
  gap: '0.6rem',
}

const inputStyle: CSSProperties = {
  minHeight: '4.4rem',
  padding: '0.8rem 1.2rem',
  border: '1px solid var(--color-border-subtle)',
  borderRadius: 'var(--Radius-Component-Input)',
  color: 'var(--color-text-primary)',
  backgroundColor: 'var(--color-surface-default1)',
}

const gridStyle: CSSProperties = {
  display: 'grid',
  gap: '1.2rem',
  gridTemplateColumns: 'repeat(auto-fill, minmax(16rem, 1fr))',
}

const cardStyle: CSSProperties = {
  display: 'grid',
  gap: '1.2rem',
  alignContent: 'space-between',
  minHeight: '15rem',
  padding: '1.6rem',
  border: '1px solid var(--color-border-subtle)',
  borderRadius: 'var(--Radius-Component-Card)',
  backgroundColor: 'var(--color-surface-default1)',
}

const iconPreviewStyle: CSSProperties = {
  alignItems: 'center',
  display: 'flex',
  minHeight: '6.4rem',
}

const codeStyle: CSSProperties = {
  display: 'block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  color: 'var(--color-text-secondary)',
  fontSize: '1.2rem',
}

const buttonStyle: CSSProperties = {
  minHeight: '3.6rem',
  border: '1px solid var(--color-border-subtle)',
  borderRadius: 'var(--Radius-Component-Button)',
  color: 'var(--color-text-primary)',
  backgroundColor: 'var(--color-surface-subtle)',
  cursor: 'pointer',
}

const getSnippet = (
  name: IconName,
  size: IconSize,
  color: IconColor,
  hoverColor: IconColor
) =>
  `<PuxIcon name="${name}" size={${size}} color="${color}" hoverColor="${hoverColor}" />`

const IconsPage: FunctionComponent = () => {
  const [query, setQuery] = useState('')
  const [categoryName, setCategoryName] = useState(
    iconCategories[0]?.name ?? 'All'
  )
  const [size, setSize] = useState<IconSize>(32)
  const [color, setColor] = useState<IconColor>('default')
  const [hoverColor, setHoverColor] = useState<IconColor>('accent')

  const icons = useMemo(
    () =>
      iconCategories
        .flatMap((category) =>
          category.icons.map((icon) => ({
            ...icon,
            category: category.name,
          }))
        )
        .filter((icon) => {
          const matchesCategory =
            categoryName === 'All' || icon.category === categoryName
          const matchesQuery = icon.name
            .toLowerCase()
            .includes(query.trim().toLowerCase())

          return matchesCategory && matchesQuery
        }),
    [categoryName, query]
  )

  const copySnippet = (name: IconName) => {
    if (!navigator?.clipboard) return

    navigator.clipboard.writeText(getSnippet(name, size, color, hoverColor))
  }

  return (
    <main style={pageStyle}>
      <div style={shellStyle}>
        <header style={headerStyle}>
          <span style={{ color: 'var(--color-text-secondary)' }}>
            Easy Software Design System
          </span>
          <h1>Icons</h1>
          <p
            style={{ maxWidth: '72rem', color: 'var(--color-text-secondary)' }}
          >
            Katalog používá stejný manifest jako komponenta PuxIcon. Hodnoty z
            CMS proto stačí poslat jako iconName, iconSize, iconColor a
            iconHoverColor.
          </p>
        </header>

        <section style={controlsStyle} aria-label='Icon controls'>
          <label style={fieldStyle}>
            Search
            <input
              style={inputStyle}
              type='search'
              value={query}
              onChange={(event) => setQuery(event.currentTarget.value)}
              placeholder='arrow, user, check...'
            />
          </label>

          <label style={fieldStyle}>
            Category
            <select
              style={inputStyle}
              value={categoryName}
              onChange={(event) => setCategoryName(event.currentTarget.value)}
            >
              <option value='All'>All</option>
              {iconCategories.map((category) => (
                <option value={category.name} key={category.name}>
                  {category.name}
                </option>
              ))}
            </select>
          </label>

          <label style={fieldStyle}>
            Size
            <select
              style={inputStyle}
              value={size}
              onChange={(event) =>
                setSize(Number(event.currentTarget.value) as IconSize)
              }
            >
              {ICON_SIZES.map((iconSize) => (
                <option value={iconSize} key={iconSize}>
                  {iconSize}px
                </option>
              ))}
            </select>
          </label>

          <label style={fieldStyle}>
            Color
            <select
              style={inputStyle}
              value={color}
              onChange={(event) =>
                setColor(event.currentTarget.value as IconColor)
              }
            >
              {ICON_COLORS.map((iconColor) => (
                <option value={iconColor} key={iconColor}>
                  {iconColor}
                </option>
              ))}
            </select>
          </label>

          <label style={fieldStyle}>
            Hover
            <select
              style={inputStyle}
              value={hoverColor}
              onChange={(event) =>
                setHoverColor(event.currentTarget.value as IconColor)
              }
            >
              {ICON_COLORS.map((iconColor) => (
                <option value={iconColor} key={iconColor}>
                  {iconColor}
                </option>
              ))}
            </select>
          </label>
        </section>

        <section style={gridStyle} aria-label='Icon catalog'>
          {icons.map((icon) => (
            <article style={cardStyle} key={`${icon.category}-${icon.name}`}>
              <div style={iconPreviewStyle}>
                <PuxIcon
                  name={icon.name}
                  size={size}
                  color={color}
                  hoverColor={hoverColor}
                  title={icon.name}
                />
              </div>
              <div>
                <strong>{icon.name}</strong>
                <div style={{ color: 'var(--color-text-secondary)' }}>
                  {icon.category}
                </div>
              </div>
              <code style={codeStyle}>
                {getSnippet(icon.name, size, color, hoverColor)}
              </code>
              <button
                type='button'
                style={buttonStyle}
                onClick={() => copySnippet(icon.name)}
              >
                Copy snippet
              </button>
            </article>
          ))}
        </section>
      </div>
    </main>
  )
}

export default IconsPage
